import "./PickupSlotPicker.scss";
import IconChevronLeft from "@tabler/icons-solidjs/dist/esm/icons/IconChevronLeft.mjs";
import IconChevronRight from "@tabler/icons-solidjs/dist/esm/icons/IconChevronRight.mjs";
import { createMemo, createSignal, For, Show } from "solid-js";
import { pickupSchedule } from "../stores/shop.store";
import { getSlotsForDay } from "../../shared/pickup-slots";
import { formatShortDate, toUtcMidnight } from "../../shared/date-utils";

const WEEKS_AHEAD = 8;
const DAY_MS = 24 * 60 * 60 * 1000;
const WEEKDAYS = ["ma", "di", "wo", "do", "vr", "za", "zo"];

type Day = {
  date: Date;
  dateString: string; // dd/mm/jjjj
  slots: string[];
};

const WEEK_NAMES = [
  "Deze week",
  "Volgende week",
  "Binnen twee weken",
  "Binnen drie weken",
  "Binnen vier weken",
  "Binnen vijf weken",
  "Binnen zes weken",
  "Binnen zeven weken",
];

// a day's column: its slots, with each run of missing times merged into one gap
function toSegments(slots: string[], times: string[]) {
  const segments: ({ time: string } | { rows: number })[] = [];
  for (const time of times) {
    const last = segments[segments.length - 1];
    if (slots.includes(time)) segments.push({ time });
    else if (last && "rows" in last) last.rows++;
    else segments.push({ rows: 1 });
  }
  return segments;
}

function weekName(week: number) {
  return WEEK_NAMES[week] ?? `Binnen ${week} weken`;
}

function formatDayMonth(date: Date) {
  return date.toLocaleDateString("nl-BE", {
    day: "numeric",
    month: "short",
    timeZone: "UTC",
  });
}

function PickupSlotPicker(props: {
  date?: string;
  time?: string;
  onSelect: (date: string, time: string) => void;
}) {
  const today = toUtcMidnight(new Date());
  const firstMonday = new Date(
    today.getTime() - ((today.getUTCDay() + 6) % 7) * DAY_MS,
  );

  const getWeek = (week: number): Day[] =>
    Array.from({ length: 7 }, (_, i) => {
      const date = new Date(firstMonday.getTime() + (week * 7 + i) * DAY_MS);
      const past = date.getTime() <= today.getTime();
      return {
        date,
        dateString: formatShortDate(date),
        slots: past ? [] : getSlotsForDay(pickupSchedule(), date),
      };
    });

  // open on the first week with slots
  const initialWeek = () => {
    for (let week = 0; week < WEEKS_AHEAD; week++) {
      if (getWeek(week).some((day) => day.slots.length)) return week;
    }
    return 0;
  };

  const [week, setWeek] = createSignal(initialWeek());
  const days = createMemo(() => getWeek(week()));

  // the previous week stays rendered while it slides out
  type Direction = "next" | "prev";
  const [direction, setDirection] = createSignal<Direction>();
  const [leaving, setLeaving] = createSignal<{
    week: number;
    direction: Direction;
  }>();

  const goToWeek = (next: number) => {
    const dir = next > week() ? "next" : "prev";
    // without animation the old week would never be removed
    if (!matchMedia("(prefers-reduced-motion: reduce)").matches)
      setLeaving({ week: week(), direction: dir });
    setDirection(dir);
    setWeek(next);
  };

  const renderWeek = (days: Day[]) => {
    if (!days.some((day) => day.slots.length))
      return (
        <p class="slotPicker_empty">
          Deze week is afhalen niet mogelijk. Kijk bij een volgende week.
        </p>
      );

    // one row per start time in this week, so equal times line up across days
    const times = [...new Set(days.flatMap((day) => day.slots))].sort();

    return (
      <div class="slotPicker_grid">
        <For each={days}>
          {(day, i) => (
            <div
              class="slotPicker_day"
              classList={{ slotPicker_day__closed: !day.slots.length }}
            >
              <div class="slotPicker_dayHeader">
                <span>{WEEKDAYS[i()]}</span>
                <b>{formatDayMonth(day.date)}</b>
              </div>
              <For each={toSegments(day.slots, times)}>
                {(segment) => {
                  if ("rows" in segment)
                    return (
                      <span
                        class="slotPicker_none"
                        style={{ "--rows": segment.rows }}
                      />
                    );
                  const time = segment.time;
                  const selected = () =>
                    props.date === day.dateString && props.time === time;
                  return (
                    <button
                      type="button"
                      class="slotPicker_slot"
                      classList={{ slotPicker_slot__selected: selected() }}
                      aria-pressed={selected()}
                      aria-label={`${WEEKDAYS[i()]} ${day.dateString} om ${time}`}
                      onClick={() => props.onSelect(day.dateString, time)}
                    >
                      {time}
                    </button>
                  );
                }}
              </For>
            </div>
          )}
        </For>
      </div>
    );
  };

  return (
    <div class="slotPicker">
      <div class="slotPicker_nav">
        <button
          type="button"
          class="btn btn__icon"
          aria-label="Vorige week"
          disabled={week() === 0}
          onClick={() => goToWeek(week() - 1)}
        >
          <IconChevronLeft />
        </button>
        <b class="slotPicker_week" aria-live="polite">
          {weekName(week())}
        </b>
        <button
          type="button"
          class="btn btn__icon"
          aria-label="Volgende week"
          disabled={week() === WEEKS_AHEAD - 1}
          onClick={() => goToWeek(week() + 1)}
        >
          <IconChevronRight />
        </button>
      </div>

      <div class="slotPicker_viewport">
        <Show when={leaving()} keyed>
          {(l) => (
            <div
              class={`slotPicker_page slotPicker_page__out-${l.direction}`}
              aria-hidden="true"
              inert
              onAnimationEnd={() => setLeaving(undefined)}
            >
              {renderWeek(getWeek(l.week))}
            </div>
          )}
        </Show>
        {/* keyed on the week so every week change mounts a new page */}
        <For each={[week()]}>
          {() => (
            <div
              class="slotPicker_page"
              classList={{
                [`slotPicker_page__in-${direction()}`]: !!direction(),
              }}
            >
              {renderWeek(days())}
            </div>
          )}
        </For>
      </div>
    </div>
  );
}

export default PickupSlotPicker;
